/**
 * The vocabulary of the alert-rule engine: the signals a rule can watch, the way it
 * compares a signal to a threshold, and the pure comparison itself.
 *
 * A rule watches one **signal** — a named, tenant-scoped number the controller knows
 * how to compute (how many devices are offline, how many deployments failed, …) — and
 * fires when that number stands in a chosen **relation** to a **threshold**. Keeping
 * the comparison pure and the signal list here (rather than in the evaluator) means
 * the same rule always fires on the same inputs, and the vocabulary is shared with the
 * console without dragging database queries into the browser bundle.
 */

/** How a rule compares its signal to its threshold. */
export const ALERT_COMPARATORS = ['GT', 'GTE', 'LT', 'LTE', 'EQ', 'NEQ'] as const;
export type AlertComparator = (typeof ALERT_COMPARATORS)[number];

/**
 * The signals a rule can watch. Each is a non-negative count the controller computes
 * per tenant; the evaluator maps each key to a query. Adding a signal is adding a key
 * here and a query there — the console needs no change to offer it.
 */
export const ALERT_SIGNALS = [
  'DEVICES_OFFLINE',
  'DEPLOYMENTS_FAILED',
  'DEVICES_DRIFTED',
  'CERTS_EXPIRING_SOON',
  'CERTS_REVOKED',
  'ALERTS_CRITICAL_OPEN',
] as const;
export type AlertSignal = (typeof ALERT_SIGNALS)[number];

/** A human-readable summary of each signal, for the console and rendered alert text. */
export const ALERT_SIGNAL_LABELS: Record<AlertSignal, string> = {
  DEVICES_OFFLINE: 'edge devices offline',
  DEPLOYMENTS_FAILED: 'deployments failed recently',
  DEVICES_DRIFTED: 'devices with configuration drift',
  CERTS_EXPIRING_SOON: 'device certificates expiring soon',
  CERTS_REVOKED: 'revoked device certificates still present',
  ALERTS_CRITICAL_OPEN: 'open critical alerts',
};

/** Whether `value` stands in the `comparator` relation to `threshold`. */
export function compareThreshold(
  value: number,
  comparator: AlertComparator,
  threshold: number,
): boolean {
  switch (comparator) {
    case 'GT':
      return value > threshold;
    case 'GTE':
      return value >= threshold;
    case 'LT':
      return value < threshold;
    case 'LTE':
      return value <= threshold;
    case 'EQ':
      return value === threshold;
    case 'NEQ':
      return value !== threshold;
  }
}

/** The symbol form of a comparator, for rendering "offline > 0". */
export function comparatorSymbol(comparator: AlertComparator): string {
  return { GT: '>', GTE: '≥', LT: '<', LTE: '≤', EQ: '=', NEQ: '≠' }[comparator];
}

/**
 * A stable one-line summary of what a rule watches, used as the alert title and — with
 * the observed value — as the alert description, so an alert reads back its own cause.
 */
export function describeRule(input: {
  signal: AlertSignal;
  comparator: AlertComparator;
  threshold: number;
}): string {
  return `${ALERT_SIGNAL_LABELS[input.signal]} ${comparatorSymbol(input.comparator)} ${input.threshold}`;
}
